import { useState, useMemo } from 'react';
import { Order } from 'models/order';
import { patientDisplayName } from 'utils/orders';

export type OrderSortColumn =
    | 'patient'
    | 'destination'
    | 'dentist'
    | 'deliveryDate'
    | 'externalDeliveryDate'
    | 'workflowStep';

export type SortDirection = 'asc' | 'desc';

const getOrderSortValue = (order: Order, column: OrderSortColumn): string | number => {
    switch (column) {
        case 'patient':
            return `${order.patient?.firstName ?? ''} ${order.patient?.lastName ?? ''}`.trim().toLowerCase();
        case 'destination':
            // DD-3620: a restricted external user receives the counterparty's id
            // without its name, so neither of these may be dereferenced blind.
            return order.deliveryDetails.destination.name?.toLowerCase() ?? '';
        case 'dentist':
            return (order.dentist?.fullName ?? '').toLowerCase();
        case 'deliveryDate':
            return new Date(order.deliveryDetails.date).getTime();
        case 'externalDeliveryDate':
            return order.deliveryDetails.externalDeliveryDate
                ? new Date(order.deliveryDetails.externalDeliveryDate).getTime()
                : 0;
        case 'workflowStep':
            return (order.workFlow?.currentStep?.name || '').toLowerCase();
    }
};

const useOrderSort = (orders: Order[], defaultColumn: OrderSortColumn = 'deliveryDate') => {
    const [sortColumn, setSortColumn] = useState<OrderSortColumn>(defaultColumn);
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const handleSort = (column: OrderSortColumn) => {
        if (column === sortColumn) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const sortedOrders = useMemo(() => {
        return [...orders].sort((orderA, orderB) => {
            const valueA = getOrderSortValue(orderA, sortColumn);
            const valueB = getOrderSortValue(orderB, sortColumn);
            if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
            if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }, [orders, sortColumn, sortDirection]);

    return { sortedOrders, sortColumn, sortDirection, handleSort };
};

export default useOrderSort;
